import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Loader2, ShieldCheck, Clock, Euro, CalendarDays, Users, CreditCard, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Countdown({ deadline }) {
  const [left, setLeft] = useState("");
  useEffect(() => {
    const tick = () => {
      const ms = new Date(deadline) - new Date();
      if (ms <= 0) { setLeft("expired"); return; }
      const h = Math.floor(ms / 3600000);
      const m = Math.floor((ms % 3600000) / 60000);
      const s = Math.floor((ms % 60000) / 1000);
      setLeft(`${h}h ${String(m).padStart(2, "0")}m ${String(s).padStart(2, "0")}s`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [deadline]);
  if (left === "expired") return <span className="text-red-300">expired</span>;
  return <span className="text-[#C5A059] font-semibold tabular-nums" data-testid="pay-countdown">{left}</span>;
}

export default function ExperiencePay() {
  const { payToken } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("loading"); // loading | error | ready | polling | confirmed
  const [errMsg, setErrMsg] = useState("");
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const polls = useRef(0);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/experiences/pay/${payToken}`);
      setInfo(data);
      if (data.status === "confirmed") { setState("confirmed"); return data; }
      setState("ready");
      return data;
    } catch (e) {
      setErrMsg(e?.response?.data?.detail || "Link is not valid.");
      setState("error");
      return null;
    }
  };

  useEffect(() => {
    (async () => {
      const data = await load();
      if (sessionId && data && data.status !== "confirmed") {
        setState("polling");
        const poll = async () => {
          polls.current += 1;
          try {
            const { data: st } = await axios.get(`${API}/deposit/status/${sessionId}`);
            if (st.payment_status === "paid") { await load(); setState("confirmed"); return; }
          } catch { /* keep polling */ }
          if (polls.current < 10) setTimeout(poll, 2500);
          else setState("ready");
        };
        poll();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payToken]);

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/experiences/pay/${payToken}/checkout`);
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start payment.");
      setBusy(false);
      load();
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white py-10 px-4">
      <div className="max-w-xl mx-auto">
        <p className="text-[10px] uppercase tracking-[0.4em] text-[#C5A059] mb-1">Juke Transfers · Experiences</p>
        <h1 className="font-display text-2xl sm:text-3xl font-light mb-6">Secure your booking</h1>

        {state === "loading" && (
          <div className="flex items-center gap-2 text-zinc-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        )}
        {state === "error" && (
          <div data-testid="pay-error" className="rounded-2xl border border-red-500/40 bg-red-500/10 p-6 text-sm text-red-200">{errMsg}</div>
        )}
        {state === "polling" && (
          <div className="rounded-2xl border border-[#C5A059]/40 bg-[#C5A059]/5 p-8 text-center" data-testid="pay-polling">
            <Loader2 className="w-8 h-8 animate-spin text-[#C5A059] mx-auto mb-3" />
            <p className="text-sm text-zinc-300">Verifying your payment…</p>
          </div>
        )}
        {state === "confirmed" && info && (
          <div data-testid="pay-confirmed" className="rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-8 text-center">
            <ShieldCheck className="w-10 h-10 text-emerald-300 mx-auto mb-3" />
            <p className="text-lg text-emerald-200">Confirmed &amp; locked ✓</p>
            <p className="text-sm text-zinc-400 mt-2">
              Deposit of €{Number(info.deposit_eur || 0).toFixed(2)} received for {info.service_title}.
              Your voucher ({info.ref}) has been emailed to you.
            </p>
          </div>
        )}

        {state === "ready" && info && (
          <>
            <div data-testid="pay-summary" className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-6 space-y-3 mb-6">
              <p className="text-xs uppercase tracking-[0.25em] text-zinc-500">Booking {info.ref}</p>
              <p className="text-xl text-white font-light">{info.service_title}</p>
              {info.supplier_name && <p className="text-xs text-zinc-500">Provided by {info.supplier_name}</p>}
              <div className="grid grid-cols-2 gap-3 text-sm text-zinc-300">
                <p className="flex items-center gap-2"><CalendarDays className="w-4 h-4 text-[#C5A059]" /> {info.service_date || "—"}{info.service_time ? ` · ${info.service_time}` : ""}</p>
                <p className="flex items-center gap-2"><Users className="w-4 h-4 text-[#C5A059]" /> {info.guests || "—"} guests</p>
                <p className="flex items-center gap-2"><Euro className="w-4 h-4 text-[#C5A059]" /> Total €{Number(info.total_price_eur || 0).toFixed(2)}</p>
                <p className="flex items-center gap-2 text-zinc-500 text-xs">Balance on site €{Number(info.balance_eur || 0).toFixed(2)}</p>
              </div>
            </div>

            {info.status === "accepted" && (
              <>
                {info.deposit_deadline_at && (
                  <p className="text-sm text-zinc-400 flex items-center gap-2 mb-4" data-testid="pay-deadline">
                    <Clock className="w-4 h-4 text-[#C5A059]" /> Time left to pay: <Countdown deadline={info.deposit_deadline_at} />
                  </p>
                )}
                <button data-testid="pay-deposit-btn" onClick={pay} disabled={busy}
                  className="w-full py-4 rounded-full bg-[#C5A059] hover:bg-[#D4AF37] text-black text-sm uppercase tracking-[0.2em] font-semibold disabled:opacity-50 flex items-center justify-center gap-2 transition-colors">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                  Pay deposit €{Number(info.deposit_eur || 0).toFixed(2)}
                </button>
                <p className="text-[11px] text-zinc-500 text-center mt-3">Secure payment via Stripe. Remaining balance paid directly to the provider on site.</p>
              </>
            )}
            {info.status === "expired" && (
              <div data-testid="pay-expired" className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-6 text-center text-sm text-amber-200">
                <X className="w-8 h-8 mx-auto mb-2" /> The payment window has expired. WhatsApp us at +385 98 934 4079 and we'll try to re-open your spot.
              </div>
            )}
            {["pending_supplier", "pending_assignment"].includes(info.status) && (
              <div className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-6 text-center text-sm text-zinc-400">
                Your request is still awaiting partner approval — you'll receive the deposit link by email once approved.
              </div>
            )}
            {["declined", "cancelled"].includes(info.status) && (
              <div className="rounded-2xl border border-red-500/40 bg-red-500/10 p-6 text-center text-sm text-red-200">
                This request is no longer active.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
